import { ApiService } from '@sparklayer-io/spark-dash-core/types/api';
import type {
  Purchase,
  PurchaseCount,
  PurchaseCountByTypeFilters,
  PurchaseCountFilters,
  PurchaseCountsByType,
  PurchaseFilters,
} from '../types';

export interface PurchasingApiContract {
  getPurchases(filters?: PurchaseFilters): Promise<Purchase[]>;
  getPurchaseCounts(filters?: PurchaseCountFilters): Promise<PurchaseCount[]>;
  getPurchaseCountsByType(filters?: PurchaseCountByTypeFilters): Promise<PurchaseCountsByType>;
}

declare module '@sparklayer-io/spark-dash-core/context/page-context' {
  interface Apis {
    purchases: PurchasingApiContract;
  }
}

/**
 * A service to interact with purchases API via the SparkLayer API.
 */
export class PurchasingApi extends ApiService implements PurchasingApiContract {
  // Accepts the widest filter set; each public method's signature constrains what callers can pass.
  private buildParams(filters?: PurchaseFilters): URLSearchParams {
    const params = new URLSearchParams();

    for (const [dimension, range] of Object.entries(filters?.dates ?? {})) {
      if (range?.gte) {
        params.append(`dates[${dimension}][gte]`, range.gte.toISOString());
      }
      if (range?.lte) {
        params.append(`dates[${dimension}][lte]`, range.lte.toISOString());
      }
    }

    if (filters?.type && filters.type !== 'all') {
      params.append('type', filters.type);
    }

    if (filters?.status) {
      params.append('status', filters.status);
    }

    if (filters?.search) {
      params.append('search', filters.search);
    }

    if (typeof filters?.limit === 'number') {
      params.append('limit', String(filters.limit));
    }

    if (filters?.customerIds?.length) {
      params.append('customer_identifiers[sparklayer]', filters.customerIds.join(','));
    }

    return params;
  }

  public getPurchases(filters?: PurchaseFilters): Promise<Purchase[]> {
    const params = this.buildParams(filters);

    // order_by is only supported by the list endpoint, and only meaningful with a date range.
    if (filters?.dates?.submitted?.gte || filters?.dates?.submitted?.lte) {
      params.append('order_by', 'calculated_submitted_at');
    }

    return this.api.proxy<Purchase[]>({
      method: 'GET',
      path: `/v1/purchases?${params.toString()}`,
    });
  }

  public getPurchaseCounts(filters?: PurchaseCountFilters): Promise<PurchaseCount[]> {
    const params = this.buildParams(filters).toString();

    return this.api.proxy<PurchaseCount[]>({
      method: 'GET',
      path: `/v1/purchases-count?${params}`,
    });
  }

  public getPurchaseCountsByType(
    filters?: PurchaseCountByTypeFilters,
  ): Promise<PurchaseCountsByType> {
    const params = this.buildParams(filters).toString();

    return this.api.proxy<PurchaseCountsByType>({
      method: 'GET',
      path: `/v1/purchases-count-by-type?${params}`,
    });
  }
}
